// Tracking & consent helpers — fires events to backend AND injects GTM/Pixel/GA4 if consent given.
import api from "./api";

let _injected = false;
let _metaLeadFired = false; // module-level dedupe within the session

export function getConsent() {
    try {
        return JSON.parse(localStorage.getItem("bm_consent") || "null");
    } catch {
        return null;
    }
}

export function setConsent(consent) {
    localStorage.setItem("bm_consent", JSON.stringify(consent));
    window.dispatchEvent(new CustomEvent("bm-consent-changed", { detail: consent }));
}

export function getUTMs() {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
    ].forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v;
    });
    // Persist if any
    if (Object.keys(utm).length) {
        localStorage.setItem("bm_utm", JSON.stringify(utm));
    }
    try {
        return JSON.parse(localStorage.getItem("bm_utm") || "{}");
    } catch {
        return {};
    }
}

export function getDevice() {
    if (typeof navigator === "undefined") return "unknown";
    const ua = navigator.userAgent;
    if (/Mobi|Android|iPhone|iPad|iPod/.test(ua)) return "mobile";
    if (/Tablet|iPad/.test(ua)) return "tablet";
    return "desktop";
}

// ---------- Meta Conversions API helpers ----------------------------

/**
 * Read a cookie by name. Returns null when not found / SSR.
 * Wrapped in try/catch so it never throws (e.g. in In-App browsers).
 */
function readCookie(name) {
    try {
        if (typeof document === "undefined") return null;
        const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]+)"));
        return match ? decodeURIComponent(match[1]) : null;
    } catch {
        return null;
    }
}

/** Meta click-id cookie (`_fbc`) — set automatically by the Pixel when fbclid present. */
export function getFBC() {
    return readCookie("_fbc");
}

/** Meta browser-id cookie (`_fbp`) — set automatically by the Pixel on first load. */
export function getFBP() {
    return readCookie("_fbp");
}

/**
 * Generate a deterministic-ish event id used for Meta CAPI deduplication.
 * Same id is sent client-side (Pixel) and server-side (CAPI) so Meta knows
 * the events represent the same conversion.
 */
export function generateEventId() {
    try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    } catch { /* ignore */ }
    // Fallback for older browsers
    return "evt-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

/**
 * Fire a Meta `Lead` event via the loaded Pixel — uses the supplied event_id
 * so the server-side CAPI call can dedupe on Meta's side. Safe on every browser:
 * never throws and never blocks UI. Only fires when marketing consent is granted
 * and `fbq` is present (= Pixel was injected after consent).
 */
export function trackMetaLead(eventId, extra = {}) {
    try {
        if (_metaLeadFired) return;
        const consent = getConsent();
        if (!consent?.marketing) return;
        if (typeof window === "undefined" || typeof window.fbq !== "function") return;
        window.fbq("track", "Lead", extra, { eventID: eventId });
        _metaLeadFired = true;
    } catch {
        /* never block UI on tracking */
    }
}

// ---------- Internal event tracking (our own backend) ----------------

export async function trackEvent(eventType, extra = {}) {
    try {
        await api.post("/tracking/event", {
            event_type: eventType,
            position: extra.position || null,
            ...getUTMs(),
            device: getDevice(),
            page: window.location.pathname,
            referrer: document.referrer || null,
            metadata: extra.metadata || null,
        });
    } catch {
        // never block UI on tracking
    }
    // Also push to dataLayer if available
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: eventType, ...extra });
    } catch {
        /* no-op */
    }
}

// ---------- Consent Mode v2 -----------------------------------------

export function applyConsentMode(consent) {
    window.dataLayer = window.dataLayer || [];
    function gtag() {
        window.dataLayer.push(arguments);
    }
    window.gtag = window.gtag || gtag;
    const adState = consent?.marketing ? "granted" : "denied";
    const analyticsState = consent?.statistics ? "granted" : "denied";
    gtag("consent", "update", {
        ad_user_data: adState,
        ad_personalization: adState,
        ad_storage: adState,
        analytics_storage: analyticsState,
        functionality_storage: "granted",
        security_storage: "granted",
    });
}

export function injectInitialConsent() {
    window.dataLayer = window.dataLayer || [];
    function gtag() {
        window.dataLayer.push(arguments);
    }
    window.gtag = window.gtag || gtag;
    // index.html already fired the synchronous default-denied — this is a
    // belt-and-braces safety net for the (very rare) case where it didn't.
    gtag("consent", "default", {
        ad_user_data: "denied",
        ad_personalization: "denied",
        ad_storage: "denied",
        analytics_storage: "denied",
        functionality_storage: "granted",
        security_storage: "granted",
        wait_for_update: 500,
    });
}

export function injectTrackingScripts(settings, consent) {
    if (_injected) return;
    if (!consent) return;
    if (!consent.statistics && !consent.marketing) return;

    // GTM
    if (settings?.gtm_id) {
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtm.js?id=${settings.gtm_id}`;
        document.head.appendChild(s);
        const ns = document.createElement("noscript");
        ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${settings.gtm_id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.appendChild(ns);
    }
    // Meta Pixel
    if (settings?.meta_pixel_id) {
        const s = document.createElement("script");
        s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${settings.meta_pixel_id}');fbq('track','PageView');`;
        document.head.appendChild(s);
    }
    // GA4 (only when no GTM is configured — otherwise GTM should manage GA4)
    if (settings?.ga4_id && !settings?.gtm_id) {
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${settings.ga4_id}`;
        document.head.appendChild(s);
        const s2 = document.createElement("script");
        s2.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js', new Date());gtag('config','${settings.ga4_id}');`;
        document.head.appendChild(s2);
    }
    _injected = true;
}
